import { api } from '@/helpers/apiClient'
import { logRequestFailure } from '@/helpers/request-outcome'
import {
  ghlConnectionDtoSchema,
  ghlConnectionOptionsDtoSchema,
  ghlVenueSettingsDtoSchema,
  type GhlConnectionDto,
  type GhlConnectionOptionsDto,
  type GhlVenueSettingsDto,
} from '@lib/dto/gohighlevel.dto'
import type { CreateGhlConnectionInput, UpdateGhlConnectionInput } from '@lib/input/gohighlevel.input'
import { leadStatsChoicesDtoSchema, type LeadStatsChoicesDto } from '@lib/dto/lead-stats.dto'
import type { IGoHighLevelService } from '@/types/GoHighLevelService.types'

const base = (venueId: string): string => `/venues/${encodeURIComponent(venueId)}/gohighlevel`

/**
 * A venue's GoHighLevel page (docs/gohighlevel.spec.md). A key goes out once, in
 * the request that saves it; nothing here ever receives one back.
 */
class GoHighLevelServiceImpl implements IGoHighLevelService {
  async settings(venueId: string): Promise<GhlVenueSettingsDto> {
    try {
      return await api.get(base(venueId), ghlVenueSettingsDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to load GoHighLevel settings', { venueId }, error)
      throw error
    }
  }

  async createConnection(venueId: string, input: CreateGhlConnectionInput): Promise<GhlConnectionDto> {
    try {
      return await api.post(`${base(venueId)}/connections`, input, ghlConnectionDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to connect a GoHighLevel sub-account', { venueId }, error)
      throw error
    }
  }

  async updateConnection(
    venueId: string,
    connectionId: string,
    input: UpdateGhlConnectionInput,
  ): Promise<GhlConnectionDto> {
    try {
      return await api.put(
        `${base(venueId)}/connections/${encodeURIComponent(connectionId)}`,
        input,
        ghlConnectionDtoSchema,
      )
    } catch (error) {
      logRequestFailure('Failed to update a GoHighLevel connection', { venueId, connectionId }, error)
      throw error
    }
  }

  async removeConnection(venueId: string, connectionId: string): Promise<void> {
    try {
      await api.del(`${base(venueId)}/connections/${encodeURIComponent(connectionId)}`)
    } catch (error) {
      logRequestFailure('Failed to remove a GoHighLevel connection', { venueId, connectionId }, error)
      throw error
    }
  }

  /** What a connection's funnel mapping is chosen from, from its last read. */
  async funnelChoices(venueId: string, connectionId: string): Promise<LeadStatsChoicesDto> {
    try {
      return await api.get(
        `${base(venueId)}/connections/${encodeURIComponent(connectionId)}/funnel-choices`,
        leadStatsChoicesDtoSchema,
      )
    } catch (error) {
      logRequestFailure('Failed to read a GoHighLevel connection’s funnel choices', { venueId, connectionId }, error)
      throw error
    }
  }

  async options(venueId: string, connectionId: string): Promise<GhlConnectionOptionsDto> {
    try {
      return await api.get(
        `${base(venueId)}/connections/${encodeURIComponent(connectionId)}/options`,
        ghlConnectionOptionsDtoSchema,
      )
    } catch (error) {
      logRequestFailure('Failed to read a GoHighLevel connection', { venueId, connectionId }, error)
      throw error
    }
  }
}

export const goHighLevelService: IGoHighLevelService = new GoHighLevelServiceImpl()
